import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Button, Collapse, Tooltip } from 'antd';
import { DeleteOutlined, FileOutlined, FileUnknownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuthState } from '../../../../auth/useAuthState';
import './styles.less';

const AssetPreview = ( { name, type, icon, size, onRemove, loading, error, className, children } ) => {
    
    const { hasPermission } = useAuthState();
    const canDelete = hasPermission( 'delete-asset' );
    
    const sizeLabel = useMemo( () => {
        if ( size && size > 0 ) {
            const sizeUnits = [
                { size: 1, units: 'bytes' },
                { size: 1024, units: 'KB' },
                { size: 1048576, units: 'MB' }
            ];

            for ( let i = sizeUnits.length - 1; i >= 0; i-- ) {
                const sizeData = sizeUnits[ i ];
                if ( size > sizeData.size ) {
                    const valueLabel = ( size / sizeData.size ).toFixed( 1 );
                    return `${ valueLabel } ${ sizeData.units }`;
                }
            }
        }
        return undefined;
    }, [ size ] );

    const autoIcon = useMemo( () => {
        switch ( type ) {
            case 'image/png':
            case 'image/jpeg':
                return <FileOutlined />;
            default:
                return <FileUnknownOutlined />;
        }
    }, [ type ] );

    const classes = classnames( [
        'file-preview',
        { 'file-preview-loading': loading },
        { 'file-preview-error': error },
        { [ className ]: Boolean( className ) },
    ] );

    return (
        <div className={ classes }>
            <Collapse
                collapsible={ !children ? 'disabled' : 'header' }
                ghost
            >
                <Collapse.Panel key="1" showArrow={ Boolean( children ) } header={
                    <div className="file-preview-header">
                        <div className="file-preview-section">
                            { loading
                                ? ( <LoadingOutlined /> ) 
                                : ( !icon ) ? autoIcon : icon
                            }
                        </div>

                        { name && (
                            <div className="file-preview-section-main">
                                { name }
                            </div>
                        ) }

                        { /* Render the main file data if not loading. */ }
                        { !loading && (
                            <>
                                <div className="file-preview-section file-preview-size">
                                    { sizeLabel }
                                </div>

                                { ( onRemove && canDelete ) && (
                                    <div className="file-preview-section">
                                        <Tooltip title="Remove" trigger="focus">
                                            <Button
                                                icon={ <DeleteOutlined /> }
                                                size={ 'small' }
                                                danger 
                                                onClick={ ( event ) => {
                                                    event.stopPropagation();
                                                    onRemove();
                                                } }
                                            >Delete</Button>
                                        </Tooltip>
                                    </div>
                                ) }
                            </>
                        ) }
                    </div>
                }>
                    { /* Render the children assuming they provide more details on the asset */ }
                    { children }
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default AssetPreview;
